import React, { useState } from 'react';
import { changePasswordWithVerification } from '../utils/api';
import { useToast } from './ui/Toast';

// Change-password form extracted from the old Security screen so it can render
// both inside the Settings page's Security tab and anywhere else it is needed.
// Behavior is unchanged: show/hide toggles, live policy checklist, verification
// of the current password, inline alerts plus a success toast.

const POLICY_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'One number', test: (p) => /[0-9]/.test(p) },
];

const passwordMeetsPolicy = (password) => POLICY_RULES.every(r => r.test(password));

const EyeIcon = ({ off }) => off ? (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
) : (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const PasswordInput = ({ id, value, onChange, autoComplete, disabled }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        disabled={disabled}
        required
        style={{ paddingRight: '42px' }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
        tabIndex={-1}
        style={{
          position: 'absolute', right: '6px', top: '50%',
          transform: 'translateY(calc(-50% + 3px))',
          background: 'transparent', border: 'none', padding: '6px',
          color: 'var(--muted)', cursor: 'pointer',
          display: 'flex', alignItems: 'center',
        }}
      >
        <EyeIcon off={show} />
      </button>
    </div>
  );
};

const ChangePasswordForm = () => {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    if (!passwordMeetsPolicy(newPassword)) {
      setError('The new password does not meet the requirements below.');
      return;
    }

    try {
      setSubmitting(true);
      await changePasswordWithVerification(currentPassword, newPassword);
      setSuccess('Password updated.');
      toast.success('Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '420px' }}>
      {error && <div className="alert error" role="alert" style={{ marginBottom: '18px' }}>{error}</div>}
      {success && <div className="alert success" role="status" style={{ marginBottom: '18px' }}>{success}</div>}

      <div className="form-group">
        <label htmlFor="sec-current">Current Password</label>
        <PasswordInput
          id="sec-current"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          disabled={submitting}
        />
      </div>

      <div className="form-group">
        <label htmlFor="sec-new">New Password</label>
        <PasswordInput
          id="sec-new"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          disabled={submitting}
        />
        {/* Live policy checklist: each rule ticks as it is met. */}
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {POLICY_RULES.map(rule => {
            const met = rule.test(newPassword);
            return (
              <li key={rule.key} style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                fontFamily: 'var(--font-sans)', fontSize: '12.5px',
                color: met ? 'var(--success)' : 'var(--muted-soft)',
                textTransform: 'none', letterSpacing: 0, fontWeight: 400,
              }}>
                {met ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  <span style={{ width: '12px', height: '12px', borderRadius: '50%', border: '1.5px solid var(--hairline)', display: 'inline-block', boxSizing: 'border-box' }} />
                )}
                {rule.label}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="form-group">
        <label htmlFor="sec-confirm">Confirm New Password</label>
        <PasswordInput
          id="sec-confirm"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          disabled={submitting}
        />
        {confirmPassword && confirmPassword !== newPassword && (
          <div style={{
            marginTop: '6px',
            fontFamily: 'var(--font-sans)', fontSize: '12.5px',
            color: 'var(--danger)', textTransform: 'none', letterSpacing: 0, fontWeight: 400,
          }}>
            Passwords do not match.
          </div>
        )}
      </div>

      <button type="submit" className="btn-primary" disabled={submitting} style={{ marginTop: '8px', alignSelf: 'flex-start' }}>
        {submitting ? 'Updating…' : 'Update Password'}
      </button>
    </form>
  );
};

export default ChangePasswordForm;
